import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { isNil, omitBy } from "lodash";
import { fetchWithCSRFTokenForRequestObject } from "Utils/fetchWithCSRFToken";

import { appendAuthParams } from "Common/TokenAuth/Utils";

import { careApiURL, CACHE_DURATION_SECONDS } from "Care/core/constants/api";

import type {
  GetCurrentUserResponse,
  GetCurrentUserParams,
  GetMedicalPlanParams,
  GetMedicalPlanResponse,
  GetPlanParams,
  VerifyResponse,
  VerifyParams,
  GetCoveragesParams,
  GetCoveragesResponse,
} from "./account.types";

export const accountApi = createApi({
  reducerPath: "accountApi",
  tagTypes: ["CurrentUser", "Verify", "MedicalPlan", "Coverages"],
  baseQuery: fetchBaseQuery({
    baseUrl: "/",
    fetchFn: fetchWithCSRFTokenForRequestObject,
  }),
  // cache for 24h - prevents search from clearing stale data
  // RTK@1 doesn't support entirely disabling this behavior, even w/ Infinity
  keepUnusedDataFor: CACHE_DURATION_SECONDS,
  endpoints: (builder) => ({
    getCurrentUser: builder.query<GetCurrentUserResponse, GetCurrentUserParams>({
      query: (params) => ({
        method: "GET",
        url: appendAuthParams(`${careApiURL}/current_user`),
        params,
      }),
      providesTags: ["CurrentUser"],
    }),
    getMedicalPlan: builder.query<GetMedicalPlanResponse, GetMedicalPlanParams>({
      query: ({ plan_id }) => ({
        method: "GET",
        // Change to our controller
        url: appendAuthParams(`${careApiURL}/medical-plan/${plan_id}`),
      }),
      providesTags: ["MedicalPlan"],
    }),
    getPlan: builder.query<GetMedicalPlanResponse, GetPlanParams>({
      query: (params) => ({
        method: "GET",
        url: appendAuthParams(`${careApiURL}/plan`),
        params: omitBy(params, isNil),
      }),
      providesTags: ["MedicalPlan"],
    }),
    verify: builder.query<VerifyResponse, VerifyParams>({
      query: (params) => ({
        url: appendAuthParams(`${careApiURL}/verify`),
        method: "POST",
        params,
      }),
      providesTags: ["Verify"],
    }),
    getCoverages: builder.query<GetCoveragesResponse, GetCoveragesParams>({
      query: ({ plan_id }) => ({
        url: appendAuthParams(`${careApiURL}/coverages/${plan_id}`),
        method: "GET",
      }),
      providesTags: ["Coverages"],
    }),
  }),
});

export const { useGetCurrentUserQuery, useGetMedicalPlanQuery, useGetPlanQuery, useVerifyQuery, useLazyVerifyQuery, useGetCoveragesQuery } = accountApi;
